// Bloque «grid»: filas × columnas (p. ej. períodos × días). En pantallas
// anchas, la tabla completa; en el celular, una columna a la vez con un
// selector (empieza por el día de hoy si es una de las columnas).

import { useMemo, useState } from "react";
import type { ProjectedRecord } from "@/convex/lib/sites/resolve";
import { cn } from "@/lib/utils";
import { formatSiteValue, type FieldMeta, type SiteBlockOf } from "../values";
import { BlockEmpty, BlockTitle } from "./shared";

const WEEKDAYS = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];
const plain = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

/** Un rango («7:00 – 7:45») no se corta en el guion; otros textos bajan de línea donde siempre. */
export const rowLabel = (row: string) => row.replace(/\s+([–—-])\s+/g, "\u00a0$1\u00a0");

/** Columna de hoy (si las columnas son días) o la primera. */
export function initialColumn(columns: string[], today: Date = new Date()): string | undefined {
  const day = WEEKDAYS[today.getDay()];
  return columns.find((c) => plain(c) === day) ?? columns[0];
}

function CellRecords({ records, fields }: { records: ProjectedRecord[]; fields: FieldMeta[] }) {
  if (!records.length) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="space-y-1.5">
      {records.map((r) => (
        <div key={r.id} className="leading-snug">
          {fields.map((f, i) => (
            <p key={f.key} className={i === 0 ? "font-medium text-foreground" : "text-xs text-muted-foreground"}>
              {formatSiteValue(r.values[f.key], f.type)}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

export function GridBlock({ block, today }: { block: SiteBlockOf<"grid">; today?: Date }) {
  const { rows, columns, cellFields } = block;
  const cells = useMemo(() => {
    const map = new Map<string, ProjectedRecord[]>();
    for (const c of block.cells) map.set(`${c.row}\u0000${c.column}`, c.records);
    return map;
  }, [block.cells]);
  const recordsAt = (row: string, column: string) => cells.get(`${row}\u0000${column}`) ?? [];

  const [chosen, setChosen] = useState<string | undefined>(() => initialColumn(columns, today));
  const column = chosen && columns.includes(chosen) ? chosen : initialColumn(columns, today);

  if (!rows.length || !columns.length) {
    return (
      <section className="space-y-3">
        {block.title && <BlockTitle>{block.title}</BlockTitle>}
        <BlockEmpty>Todavía no hay nada para mostrar.</BlockEmpty>
      </section>
    );
  }

  return (
    <section className="space-y-3" aria-label={block.title}>
      {block.title && <BlockTitle>{block.title}</BlockTitle>}

      {/* Pantallas anchas: la tabla completa. */}
      <div className="hidden overflow-x-auto rounded-lg border bg-card @min-[48rem]:block" data-testid="site-grid-table">
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b bg-muted/60">
              <th scope="col" className="w-36 px-3 py-2.5" />
              {columns.map((c) => (
                <th key={c} scope="col" className="px-3 py-2.5 text-left font-medium text-muted-foreground">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row} className="border-b last:border-0">
                <th scope="row" className="px-3 py-2.5 text-left align-top font-medium tabular-nums text-muted-foreground">
                  {rowLabel(row)}
                </th>
                {columns.map((c) => (
                  <td key={c} className="px-3 py-2.5 align-top">
                    <CellRecords records={recordsAt(row, c)} fields={cellFields} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Celular: una columna a la vez. */}
      <div className="space-y-3 @min-[48rem]:hidden" data-testid="site-grid-mobile">
        <div className="site-scroll-x -mx-4 flex gap-2 overflow-x-auto px-4 pb-1" role="tablist" aria-label="Elegir columna">
          {columns.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={c === column}
              onClick={() => setChosen(c)}
              className={cn(
                "shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                c === column
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground hover:bg-accent",
              )}
            >
              {c}
            </button>
          ))}
        </div>
        <ul className="divide-y rounded-lg border bg-card" role="tabpanel" aria-label={column} data-testid="site-grid-column">
          {rows.map((row) => (
            <li key={row} className="flex gap-3 px-4 py-3">
              {/* Ancho para una hora («7:00 – 7:45»); un texto más largo baja de línea. */}
              <span className="w-28 shrink-0 text-sm font-medium tabular-nums leading-snug text-muted-foreground">
                {rowLabel(row)}
              </span>
              <div className="min-w-0 flex-1 text-sm">
                <CellRecords records={column ? recordsAt(row, column) : []} fields={cellFields} />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
